import { useEffect, useRef, useState } from 'react';
import { ClipboardCheck, Clock, CheckCircle, XCircle, AlertCircle } from 'lucide-react';
import { Card, CardContent, CardHeader } from '../components/ui/Card';
import { Button } from '../components/ui/Button';
import { Badge } from '../components/ui/Badge';
import { Skeleton } from '../components/ui/Skeleton';
import { Input } from '../components/ui/Input';
import { apiClient } from '../lib/api-client';
import { PageHeader } from '../components/ui/PageHeader';
import { PillTabs, PillTabsList, PillTabsTrigger, PillTabsContent } from '../components/ui/PillTabs';
import { useActivityHeartbeat } from '../hooks/useActivityHeartbeat';
import { ToastContainer } from '../components/ui/Toast';
import { InlineConfirmation } from '../components/ui/InlineConfirmation';
import { useCacheEvents } from '../hooks/useCacheEvents';
import { useAuth } from '../contexts/AuthContext';

interface Approval {
  id: string;
  type: 'workflow_approval' | 'schedule_approval' | 'external_ms_approval';
  status: 'pending' | 'approved' | 'rejected' | 'expired';
  createdAt: string;
  updatedAt: string;
  expiresAt: string;
  requestedBy: {
    userId: string;
    email: string;
    name: string;
  };
  workflow: {
    name: string;
    displayName: string;
    runId: string;
    tenantCode: string;
    parameters: Record<string, any>;
  };
  approvers: Array<{
    userId: string;
    email: string;
    name: string;
    status: 'pending' | 'approved' | 'rejected';
    respondedAt?: string;
    comment?: string;
  }>;
  plannerTaskId?: string;
}

export function Approvals() {
  const { user } = useAuth();
  const [approvals, setApprovals] = useState<Approval[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [filter, setFilter] = useState<'all' | 'pending' | 'approved' | 'rejected' | 'expired'>('all');
  const [search, setSearch] = useState('');
  const [comment, setComment] = useState<Record<string, string>>({});
  const [actionInProgress, setActionInProgress] = useState<string | null>(null);
  const [toasts, setToasts] = useState<Array<{ id: string; message: string; type?: 'success' | 'error' | 'warning' | 'info' }>>([]);
  const [confirmApprove, setConfirmApprove] = useState<string | null>(null);
  const [confirmReject, setConfirmReject] = useState<string | null>(null);
  // Prevent UI from reverting to "pending" while backend/Graph propagation catches up.
  const localStatusOverrideRef = useRef<Record<string, { status: Approval['status']; at: number }>>({});

  // Mark approvals page as active so the backend refresh scheduler can sync portal <-> Teams for this user.
  useActivityHeartbeat('cache:approvals', true, 5000);

  // Instant updates: when a new approval is created (or approvals cache changes) for this user, refetch immediately.
  useCacheEvents({
    enabled: true,
    cacheNames: ['approvals'],
    onEvent: (e) => {
      const email = String((user as any)?.email || '').trim().toLowerCase();
      const affected = Array.isArray(e.affectedUsers) ? e.affectedUsers.map((x) => String(x).trim().toLowerCase()) : [];
      if (affected.length > 0 && email && !affected.includes(email)) return;
      void loadApprovals(true);
    },
  });

  const addToast = (message: string, type: 'success' | 'error' | 'warning' | 'info' = 'info') => {
    const id = Date.now().toString();
    setToasts(prev => [...prev, { id, message, type }]);
  };

  const removeToast = (id: string) => {
    setToasts(prev => prev.filter(t => t.id !== id));
  };

  useEffect(() => {
    loadApprovals();
  }, []);

  const loadApprovals = async (forceRefresh: boolean = false) => {
    try {
      const data = forceRefresh ? await apiClient.refreshApprovals() : await apiClient.getApprovals();
      // Apply short-lived local overrides (approved/rejected) if backend still returns pending.
      const overrides = localStatusOverrideRef.current || {};
      const now = Date.now();
      const nextData = (Array.isArray(data) ? data : []).map((a: Approval) => {
        const ov = overrides[a.id];
        if (!ov) return a;
        // Keep override for up to 2 minutes, unless backend already matches.
        if (a.status === ov.status) {
          delete overrides[a.id];
          return a;
        }
        if (a.status === 'pending' && (ov.status === 'approved' || ov.status === 'rejected') && now - ov.at < 2 * 60 * 1000) {
          return { ...a, status: ov.status, updatedAt: new Date().toISOString() };
        }
        // Override expired; drop it.
        delete overrides[a.id];
        return a;
      });
      setApprovals(nextData);
    } catch (err: any) {
      console.error('Failed to load approvals:', err);
    } finally {
      setIsLoading(false);
    }
  };

  const handleApprove = async (approval: Approval) => {
    setActionInProgress(approval.id);
    try {
      await apiClient.approveApproval(approval.id, comment[approval.id] || undefined);
      addToast('Approval approved! The workflow will execute shortly.', 'success');
      // Optimistic local update so the card changes immediately (even if cache refresh is slow).
      localStatusOverrideRef.current[approval.id] = { status: 'approved', at: Date.now() };
      setApprovals((prev) =>
        prev.map((a) =>
          a.id === approval.id ? { ...a, status: 'approved', updatedAt: new Date().toISOString() } : a
        )
      );
      // Fire-and-forget: refresh cache in background (do not block UI and do not risk reverting).
      void loadApprovals(true);
      setComment((prev) => {
        const updated = { ...prev };
        delete updated[approval.id];
        return updated;
      });
    } catch (err: any) {
      addToast(`Failed to approve: ${err.message}`, 'error');
    } finally {
      setActionInProgress(null);
      setConfirmApprove(null);
    }
  };

  const handleReject = async (approval: Approval) => {
    const userComment = comment[approval.id] || '';
    if (!userComment.trim()) {
      addToast('Please provide a comment explaining why you are rejecting this request.', 'warning');
      setConfirmReject(null);
      return;
    }

    setActionInProgress(approval.id);
    try {
      await apiClient.rejectApproval(approval.id, userComment);
      addToast('Approval rejected.', 'success');
      // Optimistic local update so the card changes immediately (even if cache refresh is slow).
      localStatusOverrideRef.current[approval.id] = { status: 'rejected', at: Date.now() };
      setApprovals((prev) =>
        prev.map((a) =>
          a.id === approval.id ? { ...a, status: 'rejected', updatedAt: new Date().toISOString() } : a
        )
      );
      // Fire-and-forget: refresh cache in background (do not block UI and do not risk reverting).
      void loadApprovals(true);
      setComment((prev) => {
        const updated = { ...prev };
        delete updated[approval.id];
        return updated;
      });
    } catch (err: any) {
      addToast(`Failed to reject: ${err.message}`, 'error');
    } finally {
      setActionInProgress(null);
      setConfirmReject(null);
    }
  };

  const getStatusIcon = (status: Approval['status']) => {
    switch (status) {
      case 'pending':
        return <Clock className="w-4 h-4 text-yellow-600 dark:text-yellow-400" />;
      case 'approved':
        return <CheckCircle className="w-4 h-4 text-green-600 dark:text-green-400" />;
      case 'rejected':
        return <XCircle className="w-4 h-4 text-red-600 dark:text-red-400" />;
      case 'expired':
        return <AlertCircle className="w-4 h-4 text-gray-600 dark:text-gray-400" />;
    }
  };

  const statusColor = {
    pending: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400',
    approved: 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400',
    rejected: 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400',
    expired: 'bg-gray-100 text-gray-800 dark:bg-gray-900/30 dark:text-gray-400',
  } as const;

  const typeLabel = (t: Approval['type']) => {
    if (t === 'workflow_approval') return 'Workflow Approval';
    if (t === 'schedule_approval') return 'Schedule Approval';
    return 'Microsoft Approval';
  };

  const buildSearchText = (a: Approval) => {
    const parts: string[] = [];
    parts.push(a.workflow?.displayName || '');
    parts.push(a.workflow?.name || '');
    parts.push(typeLabel(a.type));
    parts.push(a.status);
    parts.push(a.requestedBy?.name || '');
    parts.push(a.requestedBy?.email || '');
    parts.push(a.workflow?.tenantCode || '');
    parts.push(a.workflow?.runId || '');
    parts.push(a.createdAt || '');
    parts.push(a.updatedAt || '');
    parts.push(a.expiresAt || '');
    for (const ap of a.approvers || []) {
      parts.push(ap.name || '');
      parts.push(ap.email || '');
      parts.push(ap.status || '');
      parts.push(ap.respondedAt || '');
      parts.push(ap.comment || '');
    }
    // Include parameters too (useful, and aligns with "search all data on cards")
    try {
      parts.push(JSON.stringify(a.workflow?.parameters || {}));
    } catch {
      // ignore
    }
    return parts.join(' ').toLowerCase();
  };

  const searchLower = search.trim().toLowerCase();

  const filteredApprovals = approvals
    .filter((a) => filter === 'all' || a.status === filter)
    .filter((a) => (searchLower ? buildSearchText(a).includes(searchLower) : true));

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const isExpiringSoon = (expiresAt: string) => {
    const expiryTime = new Date(expiresAt).getTime();
    const now = Date.now();
    const hoursRemaining = (expiryTime - now) / (1000 * 60 * 60);
    return hoursRemaining > 0 && hoursRemaining < 4;
  };

  if (isLoading) {
    return (
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Skeleton variant="circle" className="w-10 h-10" />
            <div className="space-y-2">
              <Skeleton className="h-5 w-28" />
              <Skeleton className="h-4 w-72" />
            </div>
          </div>
          <Skeleton className="h-6 w-24" />
        </div>

        <div className="flex gap-2 flex-wrap">
          {[1, 2, 3, 4, 5].map((i) => (
            <Skeleton key={i} className="h-9 w-20 rounded-full" />
          ))}
        </div>

        <div>
          <Skeleton className="h-10 w-full rounded-lg" />
        </div>

        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
          {[1, 2, 3, 4, 5, 6].map((i) => (
            <div
              key={i}
              className="border border-gray-200 dark:border-gray-700 rounded-lg p-4 bg-white dark:bg-gray-800 flex flex-col h-full"
            >
              <div className="flex items-start justify-between mb-3 gap-3">
                <div className="flex-1 min-w-0 space-y-2">
                  <Skeleton className="h-5 w-3/4" />
                  <Skeleton className="h-4 w-1/2" />
                </div>
                <Skeleton className="h-6 w-20 rounded-full" />
              </div>

              <div className="space-y-3 flex-1">
                <div className="space-y-2">
                  <Skeleton className="h-4 w-2/3" />
                  <Skeleton className="h-4 w-1/2" />
                  <Skeleton className="h-4 w-3/5" />
                </div>

                <div className="space-y-2">
                  <Skeleton className="h-3 w-24" />
                  <Skeleton className="h-16 w-full rounded-md" />
                </div>

                <div className="space-y-2">
                  <Skeleton className="h-3 w-28" />
                  <div className="flex flex-wrap gap-2">
                    <Skeleton className="h-6 w-24 rounded-full" />
                    <Skeleton className="h-6 w-20 rounded-full" />
                    <Skeleton className="h-6 w-28 rounded-full" />
                  </div>
                </div>
              </div>

              <div className="mt-4 flex gap-2">
                <Skeleton className="h-9 w-24" />
                <Skeleton className="h-9 w-24" />
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  const pendingCount = approvals.filter(a => a.status === 'pending').length;
  const approvedCount = approvals.filter(a => a.status === 'approved').length;
  const rejectedCount = approvals.filter(a => a.status === 'rejected').length;
  const expiredCount = approvals.filter(a => a.status === 'expired').length;

  return (
    <div className="space-y-4">
      <PageHeader
        icon={ClipboardCheck}
        description="Review and approve workflow execution requests"
        count={approvals.length}
        countLabel="approvals"
      />

      {/* Filter tabs */}
      <PillTabs value={filter} onValueChange={(value) => setFilter(value as any)}>
        <PillTabsList>
          <PillTabsTrigger value="all">
            All
            <Badge variant="outline" className="ml-1.5">{approvals.length}</Badge>
          </PillTabsTrigger>
          <PillTabsTrigger value="pending">
            Pending
            <Badge variant="outline" className="ml-1.5">{pendingCount}</Badge>
          </PillTabsTrigger>
          <PillTabsTrigger value="approved">
            Approved
            <Badge variant="outline" className="ml-1.5">{approvedCount}</Badge>
          </PillTabsTrigger>
          <PillTabsTrigger value="rejected">
            Rejected
            <Badge variant="outline" className="ml-1.5">{rejectedCount}</Badge>
          </PillTabsTrigger>
          <PillTabsTrigger value="expired">
            Expired
            <Badge variant="outline" className="ml-1.5">{expiredCount}</Badge>
          </PillTabsTrigger>
        </PillTabsList>

        <PillTabsContent value={filter} className="mt-4">
      {/* Search */}
      <div className="mb-4">
        <Input
          placeholder="Search approvals (name, type, requester, approvers, comments, etc.)"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
      </div>

      {filteredApprovals.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center">
            <ClipboardCheck className="w-12 h-12 mx-auto text-gray-400 dark:text-gray-600 mb-4" />
            <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-2">
              No approvals found
            </h3>
            <p className="text-sm text-gray-500 dark:text-gray-400">
              {filter === 'all' 
                ? 'There are no approvals at this time.'
                : `No ${filter} approvals found.`}
            </p>
          </CardContent>
        </Card>
      ) : (
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
          {filteredApprovals.map((approval) => {
            // Only approvers can action an approval. Requesters can view their own requests but cannot approve/reject.
            const canAction = approval.status === 'pending';
            const currentUserEmail = String((user as any)?.email || '').trim().toLowerCase();
            const isApproverForThis =
              !!currentUserEmail &&
              Array.isArray(approval.approvers) &&
              approval.approvers.some(
                (a) => String(a?.email || a?.userId || '').trim().toLowerCase() === currentUserEmail
              );
            const canActionThisApproval = approval.status === 'pending' && isApproverForThis;
            const responders = (approval.approvers || [])
              .filter((a) => a.status !== 'pending')
              .sort((a, b) => new Date(b.respondedAt || 0).getTime() - new Date(a.respondedAt || 0).getTime());
            return (
              <div
                key={approval.id}
                className="border border-gray-200 dark:border-gray-700 rounded-lg p-4 hover:border-gray-300 dark:hover:border-gray-600 transition-colors bg-white dark:bg-gray-800 flex flex-col h-full"
              >
                <div className="flex items-start justify-between mb-3 gap-3">
                  <div className="flex-1 min-w-0">
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 truncate">
                      {approval.workflow.displayName}
                    </h3>
                    <p className="text-sm text-gray-500 dark:text-gray-400 mt-1 line-clamp-2">
                      {typeLabel(approval.type)}
                    </p>
                  </div>
                  <div className="flex items-center gap-2">
                    {getStatusIcon(approval.status)}
                  </div>
                </div>

                <div className="flex items-center gap-2 mb-3 flex-wrap">
                  <Badge variant="outline" className={statusColor[approval.status]}>
                    {approval.status === 'pending'
                      ? 'Pending'
                      : approval.status === 'approved'
                        ? 'Approved'
                        : approval.status === 'rejected'
                          ? 'Rejected'
                          : 'Expired'}
                  </Badge>
                  <Badge variant="outline" className="text-xs">
                    {typeLabel(approval.type)}
                  </Badge>
                  {isExpiringSoon(approval.expiresAt) && approval.status === 'pending' && (
                    <Badge variant="outline" className="text-xs bg-orange-50 text-orange-700 dark:bg-orange-900/30 dark:text-orange-400 border-orange-200 dark:border-orange-800">
                      Expiring Soon
                    </Badge>
                  )}
                </div>

                <div className="space-y-2 text-sm">
                  <div className="flex flex-col gap-0.5 sm:flex-row sm:items-center sm:justify-between sm:gap-2">
                    <span className="text-gray-500 dark:text-gray-400">Requested by</span>
                    <span
                      className="font-medium text-gray-900 dark:text-gray-100 break-words sm:text-right"
                      title={approval.requestedBy.email}
                    >
                      {approval.requestedBy.name
                        ? `${approval.requestedBy.name} (${approval.requestedBy.email})`
                        : approval.requestedBy.email}
                    </span>
                  </div>
                  <div className="flex flex-col gap-0.5 sm:flex-row sm:items-center sm:justify-between sm:gap-2">
                    <span className="text-gray-500 dark:text-gray-400">Requested at</span>
                    <span className="font-medium text-gray-900 dark:text-gray-100 sm:text-right">
                      {formatDate(approval.createdAt)}
                    </span>
                  </div>
                  <div className="flex flex-col gap-0.5 sm:flex-row sm:items-center sm:justify-between sm:gap-2">
                    <span className="text-gray-500 dark:text-gray-400">Expires</span>
                    <span className="font-medium text-gray-900 dark:text-gray-100 sm:text-right">
                      {formatDate(approval.expiresAt)}
                    </span>
                  </div>
                </div>

                {/* Decision / responses */}
                {responders.length > 0 && (
                  <div className="mt-3 pt-3 border-t border-gray-200 dark:border-gray-700">
                    <div className="text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400 mb-2">
                      Decision
                    </div>
                    <div className="space-y-2 text-sm">
                      {responders.map((r) => (
                        <div key={`${approval.id}-${r.email}-${r.respondedAt || ''}`} className="rounded-md bg-gray-50 dark:bg-gray-900/30 p-2">
                          <div className="flex flex-col gap-0.5 sm:flex-row sm:items-center sm:justify-between sm:gap-2">
                            <span className="font-medium text-gray-900 dark:text-gray-100 break-words" title={r.email}>
                              {r.name ? `${r.name} (${r.email})` : r.email}
                            </span>
                            <span className="text-xs text-gray-500 dark:text-gray-400 sm:whitespace-nowrap">
                              {r.respondedAt ? formatDate(r.respondedAt) : ''}
                            </span>
                          </div>
                          <div className="mt-1 flex items-center gap-2 flex-wrap">
                            <Badge
                              variant="outline"
                              className={
                                r.status === 'approved'
                                  ? 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400'
                                  : 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400'
                              }
                            >
                              {r.status === 'approved' ? 'Approved' : 'Rejected'}
                            </Badge>
                            {r.comment ? (
                              <span className="text-xs text-gray-700 dark:text-gray-300 break-words" title={r.comment}>
                                {r.comment}
                              </span>
                            ) : (
                              <span className="text-xs text-gray-500 dark:text-gray-400">No comment</span>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {canActionThisApproval && (
                  <div className="mt-auto pt-3 border-t border-gray-200 dark:border-gray-700 space-y-2">
                    <Input
                      placeholder="Optional comment..."
                      value={comment[approval.id] || ''}
                      onChange={(e) => setComment({ ...comment, [approval.id]: e.target.value })}
                    />
                    {confirmApprove === approval.id ? (
                      <InlineConfirmation
                        message={`Approve "${approval.workflow.displayName}"?`}
                        onConfirm={() => handleApprove(approval)}
                        onCancel={() => setConfirmApprove(null)}
                        isLoading={actionInProgress === approval.id}
                        loadingText="Approving..."
                        confirmText="Approve"
                        variant="success"
                      />
                    ) : confirmReject === approval.id ? (
                      <InlineConfirmation
                        message={`Reject "${approval.workflow.displayName}"?`}
                        onConfirm={() => handleReject(approval)}
                        onCancel={() => setConfirmReject(null)}
                        isLoading={actionInProgress === approval.id}
                        loadingText="Rejecting..."
                        confirmText="Reject"
                        variant="danger"
                      />
                    ) : (
                      <div className="flex flex-col gap-2 sm:flex-row">
                        <Button
                          variant="outline"
                          size="sm"
                          className="w-full sm:flex-1 text-red-600 hover:text-red-700 dark:text-red-400 dark:hover:text-red-300"
                          onClick={() => setConfirmReject(approval.id)}
                          disabled={actionInProgress === approval.id}
                        >
                          <XCircle className="w-4 h-4 mr-2" />
                          Reject
                        </Button>
                        <Button
                          size="sm"
                          className="w-full sm:flex-1 bg-green-600 hover:bg-green-700 text-white"
                          onClick={() => setConfirmApprove(approval.id)}
                          disabled={actionInProgress === approval.id}
                        >
                          <CheckCircle className="w-4 h-4 mr-2" />
                          Approve
                        </Button>
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
        </PillTabsContent>
      </PillTabs>

      <ToastContainer toasts={toasts} onRemove={removeToast} />
    </div>
  );
}
